import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import { fetchCustomerLedger } from "../../../store/Slices/CustomerLedgerSlice";
import { fetchCustomerItemSummary } from "../../../store/Slices/CustomerItemSummarySlice";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import { CustomerCompleteLedgerColumns } from "../../../assets/Columns/CustomerCompleteLedgerColumns";
import ReceivableTxnCardList from "../../../components/Receivables/ReceivableTxnCardList";
import ReceivableCustomerDetailHero from "../../../components/Receivables/ReceivableCustomerDetailHero";
import EditReceivableContactModal from "../../../components/Receivables/EditReceivableContactModal";
import { FaArrowAltCircleLeft } from "react-icons/fa";
import { BsSearch } from "react-icons/bs";
import { FiInbox } from "react-icons/fi";

import { getAppBasePath } from "../../../utils/appPaths";
import { sortLedgerRowsByDateDesc } from "../../../utils/sortLedgerRows";
const TXN_TABS = [
  { id: "all", label: "All In/Out", short: "All" },
  { id: "sale", label: "Sales (In)", short: "Sales" },
  { id: "payment", label: "Payments (Out)", short: "Pay" },
  { id: "return", label: "Returns (Out)", short: "Return" },
];

const isReturnRow = (r) =>
  Number(r?.dr) > 0 && r?.name && r.name !== "-";

const isPaymentRow = (r) =>
  Number(r?.dr) > 0 && (!r?.name || r.name === "-");

const ReceivableDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [txnTab, setTxnTab] = useState("all");
  const [editOpen, setEditOpen] = useState(false);

  const CustomersState = useSelector((state) => state.Customers);
  const CustomerLedgers = useSelector((state) => state.CustomerLedgers);
  const CustomerItemSummaryState = useSelector(
    (state) => state.CustomerItemSummaryState
  );

  const customer = useMemo(
    () => (CustomersState.data || []).find((c) => c._id === customerId),
    [CustomersState.data, customerId]
  );

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerLedger(customerId));
      dispatch(fetchCustomerItemSummary(customerId));
    }
  }, [customerId, dispatch]);

  const isCartonUnit = (unit) => {
    const u = String(unit || "").trim().toLowerCase();
    return u === "box" || u === "ct" || u === "carton";
  };

  const purchaseItemRows = useMemo(() => {
    const items = CustomerItemSummaryState?.data?.data;
    if (!Array.isArray(items)) return [];
    return items.filter((dt) => dt.name !== "Total" && Number(dt.qty) > 0);
  }, [CustomerItemSummaryState?.data]);

  const totalBoxQty = useMemo(() => {
    return purchaseItemRows
      .filter((dt) => isCartonUnit(dt.type))
      .reduce((acc, dt) => acc + Number(dt.qty || 0), 0);
  }, [purchaseItemRows]);

  const allRows = useMemo(() => {
    const d = CustomerLedgers?.data;
    return sortLedgerRowsByDateDesc(Array.isArray(d?.All) ? d.All : []);
  }, [CustomerLedgers?.data]);

  const ledgerRows = useMemo(() => {
    const d = CustomerLedgers?.data;
    if (txnTab === "sale") {
      return sortLedgerRowsByDateDesc(
        Array.isArray(d?.Item) ? d.Item : allRows.filter((r) => Number(r.cr) > 0)
      );
    }
    if (txnTab === "payment") {
      return sortLedgerRowsByDateDesc(
        Array.isArray(d?.Cash) ? d.Cash : allRows.filter(isPaymentRow)
      );
    }
    if (txnTab === "return") {
      return sortLedgerRowsByDateDesc(
        Array.isArray(d?.Return) ? d.Return : allRows.filter(isReturnRow)
      );
    }
    return allRows;
  }, [allRows, txnTab, CustomerLedgers?.data]);

  const columns = useMemo(() => {
    if (txnTab === "payment") {
      return CustomerCompleteLedgerColumns.filter(
        (c) => c.id !== "cr" && c.id !== "qty" && c.id !== "price" && c.id !== "name"
      );
    }
    if (txnTab === "sale") {
      return CustomerCompleteLedgerColumns.filter((c) => c.id !== "dr");
    }
    if (txnTab === "return") {
      return CustomerCompleteLedgerColumns.filter(
        (c) => c.id !== "cr" && c.id !== "desc"
      );
    }
    return CustomerCompleteLedgerColumns;
  }, [txnTab]);

  const base = getAppBasePath();

  if (CustomersState.loading && !customer) {
    return (
      <BodyWrapper>
        <FetchingLoading />
      </BodyWrapper>
    );
  }

  if (!customer) {
    return (
      <BodyWrapper>
        <div className="text-center py-12 text-muted">Customer not found.</div>
      </BodyWrapper>
    );
  }

  return (
    <BodyWrapper>
      <Header title="Customer ledger" desc="Sale & payment history" Back>
        <button
          type="button"
          className="flex items-center gap-2 text-main font-semibold text-sm"
          onClick={() => navigate(`${base}/receivables`)}
        >
          <FaArrowAltCircleLeft />
          <span className="max480:hidden">Back to list</span>
          <span className="hidden max480:inline">Back</span>
        </button>
      </Header>

      <ReceivableCustomerDetailHero
        customer={customer}
        totalBoxQty={totalBoxQty}
        loadingBoxQty={CustomerItemSummaryState.loading}
        onEdit={() => setEditOpen(true)}
      />

      <EditReceivableContactModal
        open={editOpen}
        setOpen={setEditOpen}
        customer={customer}
        onUpdated={() => dispatch(fetchCustomers())}
      />

      {purchaseItemRows.length > 0 && (
        <div className="mb-3 rounded-xl border border-line bg-card shadow-soft overflow-hidden">
          <div className="px-3 py-2 border-b border-line bg-subtle">
            <h3 className="text-[11px] font-bold text-ink uppercase tracking-wide">
              Items taken (qty)
            </h3>
          </div>
          <ul className="divide-y divide-line max-h-48 overflow-y-auto">
            {purchaseItemRows.map((item) => (
              <li
                key={`${item.name}-${item.type}`}
                className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs"
              >
                <span className="font-medium text-ink truncate">
                  {item.name}
                  {isCartonUnit(item.type) && (
                    <span className="ml-2 text-[10px] font-bold uppercase text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">
                      Carton
                    </span>
                  )}
                </span>
                <span className="shrink-0 tabular-nums font-semibold text-ink">
                  {Number(item.qty || 0).toLocaleString("en-PK")}{" "}
                  <span className="text-muted font-normal">{item.type}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-3">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <h3 className="text-[11px] font-bold text-ink uppercase tracking-wide">
            Transactions
          </h3>
          {!CustomerLedgers.loading && (
            <span className="text-[10px] text-muted font-medium">
              {ledgerRows.filter((r) => r.desc !== "Total").length} entries
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-2.5">
          {TXN_TABS.map(({ id, label, short }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTxnTab(id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-all ${
                txnTab === id
                  ? "bg-main text-white border-main shadow-soft"
                  : "bg-card text-inkSecondary border-line hover:border-main/40"
              }`}
            >
              <span className="max720:hidden">{label}</span>
              <span className="hidden max720:inline">{short}</span>
            </button>
          ))}
        </div>

        <div className="relative mb-2.5">
          <BsSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none text-sm" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search transactions..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-line bg-card text-sm shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15 transition-all"
          />
        </div>
      </div>

      {CustomerLedgers.loading ? (
        <FetchingLoading />
      ) : ledgerRows.filter((r) => r.desc !== "Total").length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
          <div className="w-14 h-14 rounded-2xl bg-main/10 flex items-center justify-center mb-3">
            <FiInbox className="text-main" size={24} />
          </div>
          <p className="font-semibold text-inkSecondary text-sm">No transactions in this tab</p>
        </div>
      ) : (
        <div className="pb-4">
          <ReceivableTxnCardList
            rows={ledgerRows}
            searchText={searchText}
            columns={columns}
          />
        </div>
      )}
    </BodyWrapper>
  );
};

export default ReceivableDetail;
